import React from 'react';
import Badge from '@amzn/awsui-components-console/badge';
import Box from '@amzn/awsui-components-console/box';
import Table from '@amzn/awsui-components-console/table';
import {
  ChangeType,
  DiffResult,
  FieldDiff,
  NO_VALUE,
} from '../utils/versionDiff';
import styles from '../StringGroupDetail.module.scss';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface VersionDiffTableProps {
  results: DiffResult[];
  /** Header for the left ("before") text column, e.g. `Previous (v2)`. */
  previousHeader: string;
  /** Header for the right ("after") text column, e.g. `Current (v3)`. */
  currentHeader: string;
  /**
   * Maps a change type to the text shown in its badge. Defaults to the
   * localized Added/Removed/Edited labels built below; the rollback view
   * overrides it to frame changes by effect ("Will reappear"). Only consulted
   * for the Change column, so a single-string view
   * (showStringColumns=false) ignores it.
   */
  badgeLabel?: (changeType: ChangeType) => string;
  /**
   * When true, the table scrolls within a bounded height instead of growing the
   * modal unboundedly — used for potentially long per-string previews.
   */
  scrollable?: boolean;
  /**
   * When true (default), the leading "String ID" and "Change" columns are
   * shown — the group-level view diffs many strings at once. The single-string
   * version diff (StringVersionDiffModal) sets this false: it compares one
   * string whose id is already in the modal header, so only the per-field
   * Field/Previous/Current columns are meaningful.
   */
  showStringColumns?: boolean;
}

/**
 * A single flattened table row: one changed field of one string. The String ID
 * and change badge render only on `isGroupStart` rows so a string with several
 * changed fields reads as one grouped block instead of repeating its id.
 */
interface DiffRow {
  key: string;
  stringId: string;
  changeType: ChangeType;
  isGroupStart: boolean;
  field: FieldDiff;
}

function getBadgeColor(changeType: ChangeType): 'green' | 'red' | 'blue' {
  switch (changeType) {
    case 'added':
      return 'green';
    case 'removed':
      return 'red';
    case 'changed':
      return 'blue';
  }
}

/** Flattens each string's field breakdown into one row per changed field. */
function toRows(results: DiffResult[]): DiffRow[] {
  return results.flatMap((result) =>
    result.fields.map((field, index) => ({
      key: `${result.stringId}#${index}#${field.field}`,
      stringId: result.stringId,
      changeType: result.changeType,
      isGroupStart: index === 0,
      field,
    })),
  );
}

/** Class for the "before" cell: strike-through on removal, dim on placeholder. */
function previousCellClass(row: DiffRow): string | undefined {
  if (row.field.previous === NO_VALUE) {
    return styles.diffPlaceholder;
  }
  return row.changeType === 'removed' ? styles.diffRemoved : undefined;
}

/** Class for the "after" cell: green on add, amber on change, dim on placeholder. */
function currentCellClass(row: DiffRow): string | undefined {
  if (row.field.current === NO_VALUE) {
    return styles.diffPlaceholder;
  }
  if (row.changeType === 'added') {
    return styles.diffAdded;
  }
  return row.changeType === 'changed' ? styles.diffChanged : undefined;
}

/**
 * Renders a per-string, per-field diff between two version snapshots as a
 * table. Shared by DiffModal (version-to-version comparison) and RollbackModal
 * (rollback impact preview) so the two never drift in layout or change-coloring.
 * Each changed field is its own row, so edits to a context note, inline markup,
 * or message format are visible even when the source text is unchanged.
 */
const VersionDiffTable: React.FC<VersionDiffTableProps> = ({
  results,
  previousHeader,
  currentHeader,
  badgeLabel,
  scrollable = false,
  showStringColumns = true,
}) => {
  const { t } = useTranslation();
  // Mapped explicitly rather than rendering the raw `changeType`: the enum is a
  // backend value and `t(changeType)` would couple resource ids to it, printing
  // a bare enum the day a new change type appears.
  const defaultBadgeLabel = (changeType: ChangeType): string => {
    switch (changeType) {
      case 'added':
        return t('sgd-change-type-added');
      case 'removed':
        return t('sgd-change-type-removed');
      case 'changed':
        // The id mirrors the enum member; its copy reads "Edited".
        return t('sgd-change-type-changed');
    }
  };
  const resolveBadgeLabel = badgeLabel ?? defaultBadgeLabel;
  const rows = toRows(results);

  // The String ID and Change columns only make sense when several strings are
  // diffed together; the single-string view omits them (see showStringColumns).
  const stringColumns = showStringColumns
    ? [
        {
          id: 'stringId',
          header: t('string-field-string-id'),
          cell: (item: DiffRow) =>
            item.isGroupStart ? (
              <Box fontWeight="bold">{item.stringId}</Box>
            ) : (
              ''
            ),
          width: 200,
        },
        {
          id: 'change',
          header: t('string-column-change'),
          cell: (item: DiffRow) =>
            item.isGroupStart ? (
              <Badge color={getBadgeColor(item.changeType)}>
                {resolveBadgeLabel(item.changeType)}
              </Badge>
            ) : (
              ''
            ),
          width: 140,
        },
      ]
    : [];

  const table = (
    <Table
      ariaLabels={{ tableLabel: t('sgd-version-diff-table-aria') }}
      columnDefinitions={[
        ...stringColumns,
        {
          id: 'field',
          header: t('sgd-column-field'),
          cell: (item: DiffRow) => (
            <span
              className={
                item.field.field === NO_VALUE
                  ? styles.diffPlaceholder
                  : undefined
              }
            >
              {item.field.field}
            </span>
          ),
          width: 180,
        },
        {
          id: 'previous',
          header: previousHeader,
          cell: (item: DiffRow) => (
            <div className={styles.diffValueCell}>
              <span className={previousCellClass(item)}>
                {item.field.previous}
              </span>
            </div>
          ),
        },
        {
          id: 'current',
          header: currentHeader,
          cell: (item: DiffRow) => (
            <div className={styles.diffValueCell}>
              <span className={currentCellClass(item)}>
                {item.field.current}
              </span>
            </div>
          ),
        },
      ]}
      items={rows}
      trackBy="key"
      wrapLines
      variant="embedded"
      empty={
        <Box textAlign="center" color="inherit" padding="l">
          {t('sgd-diff-no-differences')}
        </Box>
      }
    />
  );

  return scrollable ? (
    <div className={styles.diffTableScroll}>{table}</div>
  ) : (
    table
  );
};

export default VersionDiffTable;
